import { atDialect } from '../../engine/dialect';

/**
 * Layout descriptor for the columns a customer list shows where its page declares nothing of its
 * own, in the shape `resolveColumns` compiles.
 */
export const CUSTOMER_COLUMNS = [
  {
    key: 'otherSections',
    label: 'SECTIONS',
    cell: 'tooltipIcon',
    icon: 'PlaylistAddCheckOutlined',
    separator: ',',
    size: { md: 0.3 },
    noCaption: true,
  },
  {
    key: 'fullName',
    label: 'FULLNAME',
    cell: 'customerName',
    noCaption: true,
    chipsKey: 'activeCampaigns',
    warningKey: 'removedOn',
    warningIcon: 'PersonOffRounded',
    warningLabel: 'NEVER_CALL_AGAIN_AGAIN',
  },
  {
    key: 'mobilePhoneNumber',
    cell: 'phone',
  },
  {
    key: 'fiscalCode',
  },
  {
    key: 'birthDate',
    label: 'BIRTH_DATE',
    cell: 'date',
  },
];

const HALF = { xs: 12, md: 6 };
const WHOLE = { xs: 12 };

/**
 * The called indicator, as everything but the key it sets: a row of buttons whose choices - what
 * each one is called, what it shows and what it applies - are the layout's.
 *
 * Which key it sets is the section's, since a wave section counts the calls of its period and a
 * daily one the calls of the day, so a section spreads this over the key it means.
 */
export const CALLED_INDICATOR = {
  type: 'segmented',
  operator: 'eq',
  options: [
    { value: null, label: 'ALL', icon: 'Circle', color: '#1D5B9D' },
    { value: 1, label: 'CALLED', icon: 'Done', color: '#1f6b2e' },
    { value: 0, label: 'TO_CALL', icon: 'Close', color: '#b91c1c' },
  ],
  size: WHOLE,
};

/**
 * Layout descriptor for the filters a customer list offers, in the field shape the engine renders.
 *
 * Every control here is drawn from what it declares: its type is an engine field, and the lists it
 * offers come from the DataSourceRegistry key in `optionsUrl`. A key that answers differently per
 * branch or per section names the runtime values it needs in `contextParameters`, which the panel
 * publishes.
 *
 * The called indicator's value is the query's own - 1, 0 or nothing - so the key it sets is the
 * section's to choose: a wave section declares `lastCallInCurrentMonth`, a daily one `called`.
 */
export const CUSTOMER_FILTERS = [
  {
    key: 'lastCallOutcome',
    type: 'select',
    label: 'LAST_CALL_OUTCOME',
    optionsUrl: 'SECTION_CALL_OUTCOMES',
    contextParameters: ['Section'],
    multiple: true,
    size: HALF,
  },
  {
    key: 'occupation',
    type: 'select',
    label: 'OCCUPATION',
    optionsUrl: 'CONTACT_OCCUPATIONS',
    multiple: true,
    size: HALF,
  },
  {
    key: 'lastProductName',
    type: 'select',
    label: 'OFFER_TYPE',
    optionsUrl: 'SECTION_OFFERS',
    contextParameters: ['BranchId', 'Section'],
    multiple: true,
    size: HALF,
  },
  {
    key: 'practiceProduct',
    type: 'select',
    label: 'DESCRIPTION_PRACTICE_PRODUCT',
    optionsUrl: 'PRACTICE_PRODUCT_CODES',
    multiple: true,
    size: HALF,
  },
  {
    key: 'subsectionId',
    type: 'select',
    label: 'SUBSECTIONS',
    optionsUrl: 'SECTION_SUBSECTIONS',
    contextParameters: ['Section'],
    multiple: true,
    size: HALF,
  },
  {
    key: 'paidInstallments',
    type: 'numberSet',
    label: 'PAID_INSTALLMENTS',
    size: HALF,
  },
  { key: 'employeer', type: 'text', label: 'EMPLOYER', size: HALF },
  {
    key: 'calledInCurrentPeriod',
    type: 'boolean',
    label: 'CURRENT_WAVE_OUTCOMES',
    operator: 'eq',
    size: HALF,
  },
  {
    key: 'distanceKm',
    type: 'numberRange',
    label: 'DISTANCE_KM_FROM_BRANCH',
    // The branch's furthest customer, published by the panel; the declared end is the fallback.
    maxSource: 'parameter',
    maxParameter: 'maxDistance',
    max: 100,
    unit: 'km',
    size: WHOLE,
  },
  {
    key: 'lastCallInCurrentMonth',
    ...CALLED_INDICATOR,
    label: 'MONTH_CALLED_INDICATOR',
  },
];

/** Layout descriptor for a customer list's sort menu, keyed like every other surface. */
export const CUSTOMER_SORTING = [
  { key: 'fullName', label: 'NAME', isDefault: true },
  { key: 'ratingValue', label: 'RATING' },
  { key: 'mobilePhoneNumber', label: 'MOBILE_PHONE' },
  { key: 'fiscalCode', label: 'FISCALCODE' },
  { key: 'birthDate', label: 'BIRTH_DATE' },
  { key: 'lastCallCompletedOn', label: 'LASTCALLDATE' },
];

/**
 * Layout descriptor for the keys a customer list's search box matches typed text against.
 *
 * `fullNameSearch` is projected by the list procedure rather than mapped on the entity, so the
 * catalogue does not hold it; the editor offers it because this layout declares it.
 */
export const CUSTOMER_SEARCH = [
  { key: 'fullNameSearch', label: 'FULLNAME' },
  { key: 'fiscalCode', label: 'FISCALCODE' },
  { key: 'mobilePhoneNumber', label: 'MOBILE_PHONE' },
];

/**
 * The surfaces a customer list offers its cog, each with the layout checked into the client.
 *
 * Called at module scope by the page that passes the result, so what the layouts are read against
 * does not change from one render to the next.
 *
 * @param {Object} own - The section's own layouts, in place of the shared ones.
 * @param {Array} own.list - The columns the list shows.
 * @param {Array} own.filters - The filters its panel offers, where the panel is engine-rendered.
 * @param {Array} own.sorting - The columns its sort menu offers.
 * @param {Array} own.search - The keys its search box matches.
 *
 * @returns {Array}
 */
export const customerSurfaces = ({
  list = null,
  filters = null,
  sorting = null,
  search = null,
} = {}) => [
  { surface: 'list', defaultLayout: atDialect(list ?? CUSTOMER_COLUMNS) },
  ...(filters
    ? [{ surface: 'filters', defaultLayout: atDialect(filters) }]
    : []),
  { surface: 'sorting', defaultLayout: atDialect(sorting ?? CUSTOMER_SORTING) },
  // The search box sits outside the filters dialog, so the menu sets it apart.
  {
    surface: 'search',
    defaultLayout: atDialect(search ?? CUSTOMER_SEARCH),
    separated: true,
  },
];

export default customerSurfaces;
